import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { EconomyDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Chip, EmptyState, PageHeader, Panel, ProgressBar, SectionHeader } from "@/design/primitives";
import { DataTable, type Column } from "@/design/DataTable";
import { relativeTime } from "@/lib/format";

/**
 * Ekonomika provozu (artboard p3b).
 *
 * **Měnou je procento týdenního limitu**, koruny jsou přepočet tarifu. Bez
 * toho se provoz nedá porovnat: worker platí předplatné se stropem, ne tokeny.
 *
 * Obrazovka poctivě přiznává, kdy číslo neplatí — na sdíleném účtu a u levných
 * sessions, které `/usage` reportuje jako nulu.
 */
export function EconomyPage() {
  const query = useQuery({
    queryKey: ["economy"],
    queryFn: api.economy,
    refetchInterval: 60_000,
  });
  const data = query.data;

  const projectColumns: Column<EconomyDTO["projects"][number]>[] = [
    {
      key: "name",
      label: cs.economy.colProject,
      width: "minmax(160px, 1fr)",
      primary: true,
      cell: (p) => (
        <Link to="/projects/$slug" params={{ slug: p.projectId }} style={{ color: "inherit" }}>
          {p.name}
        </Link>
      ),
    },
    {
      key: "pct",
      label: cs.economy.colPct,
      width: "150px",
      cell: (p) => (
        <span className="flex items-center gap-2">
          <ProgressBar
            value={data && data.totalPct > 0 ? (p.pct / data.totalPct) * 100 : 0}
            className="w-[80px]"
          />
          <span className="af-mono text-[11px]" style={{ color: "var(--accent)" }}>
            {p.pct.toFixed(1).replace(".", ",")} %
          </span>
        </span>
      ),
    },
    {
      key: "price",
      label: cs.economy.colPrice,
      width: "110px",
      align: "right",
      cell: (p) => (
        <span className="af-mono" style={{ color: "var(--text-secondary)" }}>
          {p.price == null ? "—" : `${p.price} Kč`}
        </span>
      ),
    },
  ];

  const taskColumns: Column<EconomyDTO["tasks"][number]>[] = [
    {
      key: "task",
      label: cs.economy.colTask,
      width: "minmax(200px, 1fr)",
      primary: true,
      cell: (t) => (
        <>
          #{t.number} {t.title}
          <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {" · "}
            {t.projectName}
          </span>
        </>
      ),
    },
    {
      key: "detail",
      label: cs.economy.colSessions,
      width: "minmax(140px, 0.8fr)",
      cell: (t) => (
        <span style={{ color: "var(--text-tertiary)" }}>
          {cs.economy.sessions(t.sessions)}
          {t.rework > 0 ? ` · ${cs.economy.rework(t.rework)}` : ""}
        </span>
      ),
    },
    {
      key: "cost",
      label: cs.economy.colPct,
      width: "150px",
      align: "right",
      cell: (t) => (
        <span className="af-mono" style={{ color: "var(--accent)" }}>
          {t.pct.toFixed(1).replace(".", ",")} %{t.price != null ? ` ≈ ${t.price} Kč` : ""}
        </span>
      ),
    },
  ];

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.economy.title}
        kicker="TÝDENNÍ OKNO"
        lead={cs.economy.lead}
        right={
          <Link to="/workers">
            <span className="af-mono text-[11px]" style={{ color: "var(--accent)" }}>
              {cs.economy.toWorkers} →
            </span>
          </Link>
        }
      />

      {!data ? (
        <Panel className="p-6">
          <EmptyState title={cs.common.loading} />
        </Panel>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Panel className="p-4" data-testid="economy-total">
              <div className="af-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: "var(--text-tertiary)" }}>
                {cs.economy.weekSpend}
              </div>
              <div className="af-display my-[3px] text-[24px] font-bold leading-none" style={{ color: "var(--text-primary)" }}>
                {data.totalPct.toFixed(1).replace(".", ",")} %
              </div>
              <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {data.totalPrice == null ? cs.economy.noPrice : `≈ ${data.totalPrice} Kč`}
              </div>
            </Panel>

            <Panel className="p-4">
              <div className="af-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: "var(--text-tertiary)" }}>
                {cs.economy.mostExpensive}
              </div>
              {mostExpensive(data) ? (
                <>
                  <div className="af-card-title my-[3px]">{mostExpensive(data)!.name}</div>
                  <div className="text-[11px]" style={{ color: "var(--warning)" }}>
                    {mostExpensive(data)!.weekPct?.toFixed(0)} % ·{" "}
                    {mostExpensive(data)!.tariff ?? cs.economy.noTariff}
                  </div>
                </>
              ) : (
                <div className="mt-2 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                  {cs.economy.noUsage}
                </div>
              )}
            </Panel>

            <Panel className="p-4">
              <div className="af-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: "var(--text-tertiary)" }}>
                {cs.economy.projectsCount}
              </div>
              <div className="af-display my-[3px] text-[24px] font-bold leading-none">
                {data.projects.length}
              </div>
              <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.economy.inWindow(data.windowDays)}
              </div>
            </Panel>

            <Panel
              tone={data.sharedAccounts.length ? "amber" : undefined}
              className="p-4"
              data-testid="economy-shared"
            >
              <div
                className="af-mono text-[9px] uppercase tracking-[0.14em]"
                style={{ color: data.sharedAccounts.length ? "var(--warning)" : "var(--text-tertiary)" }}
              >
                {cs.economy.accuracy}
              </div>
              <div className="mt-1.5 text-[12px]" style={{ color: "var(--text-secondary)" }}>
                {data.sharedAccounts.length
                  ? cs.economy.sharedWarning(data.sharedAccounts.join(", "))
                  : cs.economy.accuracyOk}
              </div>
            </Panel>
          </div>

          <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-[1fr_1fr]">
            <Panel className="p-5" data-testid="economy-projects">
              <SectionHeader label={cs.economy.projectCost} sub={cs.economy.thisWeek} />
              <div className="mt-3">
                <DataTable
                  columns={projectColumns}
                  rows={data.projects}
                  rowKey={(p) => p.projectId}
                  stackBelow={1024}
                  empty={<EmptyState title={cs.economy.noData} note={cs.economy.noDataNote} />}
                />
              </div>
              <div className="mt-3 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                ⚠ {cs.economy.roundingNote}
              </div>
            </Panel>

            <Panel className="p-5" data-testid="economy-tasks">
              <SectionHeader label={cs.economy.taskCost} sub={cs.economy.thisWeek} />
              <div className="mt-3">
                <DataTable
                  columns={taskColumns}
                  rows={data.tasks}
                  rowKey={(t) => `${t.projectName}#${t.number}`}
                  stackBelow={1024}
                  empty={<EmptyState title={cs.economy.noData} />}
                />
              </div>
            </Panel>
          </div>

          <Panel className="mt-4 p-5" data-testid="economy-workers">
            <SectionHeader label={cs.economy.machines} sub={cs.economy.machinesNote} />
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {data.workers.map((w) => (
                <div
                  key={w.id}
                  className="px-4 py-3"
                  style={{
                    borderRadius: "var(--radius-box)",
                    border: "1px solid var(--border-default)",
                    background: "var(--surface)",
                  }}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="af-card-title">{w.name}</span>
                    {w.weekPct != null ? (
                      <Chip tone={w.weekPct >= 85 ? "red" : w.weekPct >= 60 ? "amber" : "green"}>
                        {Math.round(w.weekPct)} %
                      </Chip>
                    ) : null}
                  </div>
                  <div className="mt-1 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                    {w.account ?? cs.economy.noAccount}
                    {w.tariff ? ` · ${w.tariff}` : ""}
                    {w.monthlyPrice != null ? ` · ${w.monthlyPrice} ${w.currency}/měs` : ""}
                  </div>
                  <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                    {w.resetsAt
                      ? cs.economy.resets(relativeTime(w.resetsAt))
                      : cs.economy.noReset}
                    {w.reportedAt ? ` · ${cs.economy.reported(relativeTime(w.reportedAt))}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function mostExpensive(data: EconomyDTO) {
  return (
    [...data.workers]
      .filter((w) => w.weekPct != null)
      .sort((a, b) => (b.weekPct ?? 0) - (a.weekPct ?? 0))[0] ?? null
  );
}
